import { z } from 'zod';
import { CHANNELS, LANGUAGES, PRODUCT_FAMILIES } from '../domain';
import { isoDateSchema } from './referentials';

// Scénarios, étapes et modèles de relance (BA-017, spec 03, spec 05.3, spec 08).

// --- Conditions d'étape ---

export const CONDITION_FIELDS = [
  'status',
  'amountDue',
  'amountRemaining',
  'daysOverdue',
  'productFamily',
  'channel',
] as const;
export const CONDITION_OPERATORS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'nin'] as const;

export type ConditionInput =
  | { all: ConditionInput[] }
  | { any: ConditionInput[] }
  | {
      field: (typeof CONDITION_FIELDS)[number];
      op: (typeof CONDITION_OPERATORS)[number];
      value: string | number | (string | number)[];
    };

const scalar = z.union([z.string().max(64), z.number()]);

// Identifiant de composant : la génération OpenAPI référence le schéma récursif au lieu de le dérouler.
export const conditionSchema: z.ZodType<ConditionInput> = z
  .lazy(() =>
    z.union([
      z.object({ all: z.array(conditionSchema).max(10) }).strict(),
      z.object({ any: z.array(conditionSchema).max(10) }).strict(),
      z
        .object({
          field: z.enum(CONDITION_FIELDS),
          op: z.enum(CONDITION_OPERATORS),
          value: z.union([scalar, z.array(scalar).max(20)]),
        })
        .strict(),
    ]),
  )
  .meta({ id: 'StepCondition' });

// --- Étapes ---

const offsetDays = z.number().int().min(-365).max(365);
const label = z.string().trim().max(120).nullable().optional();
/** BA-079 : décalage compté depuis l'échéance (défaut) ou depuis le jour du préavis. */
const offsetFrom = z.enum(['due', 'notice']).optional();

export const messageStepSchema = z
  .object({
    kind: z.literal('message'),
    offsetDays,
    offsetFrom,
    /** BA-080 : `preferred` essaie d'abord le canal préféré de l'assuré. */
    channelMode: z.enum(['fixed', 'preferred']).optional(),
    channelPrimary: z.enum(CHANNELS),
    templateId: z.string().min(1),
    channelFallback: z.enum(CHANNELS).nullable().optional(),
    fallbackTemplateId: z.string().min(1).nullable().optional(),
    condition: conditionSchema.nullable().optional(),
    waitDeliveryHours: z.number().int().min(1).max(48).optional(),
    label,
  })
  .refine((s) => !s.channelFallback || s.channelFallback !== s.channelPrimary, {
    message: 'Le canal de secours doit différer du canal principal',
    path: ['channelFallback'],
  })
  .refine((s) => !s.channelFallback === !s.fallbackTemplateId, {
    message: 'Canal et modèle de secours vont ensemble',
    path: ['fallbackTemplateId'],
  });

export const escalationStepSchema = z.object({
  kind: z.literal('escalation'),
  offsetDays,
  offsetFrom,
  condition: conditionSchema.nullable().optional(),
  label,
});

export const stepInputSchema = z.union([messageStepSchema, escalationStepSchema]);
export type StepInput = z.infer<typeof stepInputSchema>;

export const scenarioInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    productFamily: z.enum(PRODUCT_FAMILIES).nullable().optional(),
    isDefault: z.boolean().optional(),
    stopOnPayment: z.boolean().optional(),
    pauseOnPromiseDays: z.number().int().min(0).max(60).optional(),
    escalateAfterStep: z.number().int().min(1).max(15).nullable().optional(),
    steps: z.array(stepInputSchema).min(1).max(15),
  })
  .refine(
    (s) =>
      s.steps.every((step, i) => {
        const previous = s.steps[i - 1];
        if (!previous) return true;
        const anchor = (x: StepInput) => x.offsetFrom ?? 'due';
        // Les étapes de préavis ouvrent le scénario ; l'ordre se vérifie par ancrage.
        if (anchor(previous) !== anchor(step)) return anchor(step) === 'due';
        return step.offsetDays >= previous.offsetDays;
      }),
    {
      message: 'Les étapes doivent être dans l’ordre chronologique (préavis en premier)',
      path: ['steps'],
    },
  );

export const stepSchema = z.object({
  orderNo: z.number().int(),
  kind: z.enum(['message', 'escalation']),
  offsetDays: z.number().int(),
  offsetFrom: z.enum(['due', 'notice']),
  channelMode: z.enum(['fixed', 'preferred']),
  channelPrimary: z.enum(CHANNELS).nullable(),
  templateId: z.string().nullable(),
  channelFallback: z.enum(CHANNELS).nullable(),
  fallbackTemplateId: z.string().nullable(),
  condition: z.unknown().nullable(),
  waitDeliveryHours: z.number().int(),
  label: z.string().nullable(),
});

export const scenarioSchema = z.object({
  id: z.string(),
  name: z.string(),
  productFamily: z.enum(PRODUCT_FAMILIES).nullable(),
  isDefault: z.boolean(),
  status: z.enum(['draft', 'published', 'archived']),
  /** Dernière version éditée. */
  version: z.number().int(),
  /** Version utilisée par les nouvelles séquences. */
  publishedVersion: z.number().int().nullable(),
  /** Le brouillon diffère de la version publiée. */
  hasDraft: z.boolean(),
  stopOnPayment: z.boolean(),
  pauseOnPromiseDays: z.number().int(),
  escalateAfterStep: z.number().int().nullable(),
  steps: z.array(stepSchema),
});

export const scenarioListSchema = z.object({
  items: z.array(scenarioSchema.omit({ steps: true })),
});
export const scenarioVersionQuerySchema = z.object({
  version: z.coerce.number().int().min(1).optional(),
});

export const scenarioPreviewQuerySchema = z.object({
  dueDate: isoDateSchema,
  version: z.coerce.number().int().min(1).optional(),
});

export const scenarioPreviewSchema = z.object({
  dueDate: z.string(),
  steps: z.array(
    z.object({
      orderNo: z.number().int(),
      kind: z.enum(['message', 'escalation']),
      label: z.string().nullable(),
      channel: z.enum(CHANNELS).nullable(),
      /** Jour visé (échéance + décalage). */
      date: z.string(),
      /** Envoi prévu dans la fenêtre du tenant (jours fériés et jours non autorisés reportés). */
      plannedAt: z.string(),
    }),
  ),
});

export const scenarioPublishSchema = z.object({
  /** Migration des séquences en cours vers la nouvelle version (BA-054). */
  applyToRunning: z.boolean().optional(),
});

/** Simulation d'un scénario sur une sélection d'échéances (BA-053), sans aucune écriture. */
export const scenarioSimulationRequestSchema = z.object({
  installmentIds: z.array(z.string().min(1).max(64)).max(5000).optional(),
  status: z.enum(['upcoming', 'due', 'overdue', 'partial']).optional(),
  dueFrom: isoDateSchema.optional(),
  dueTo: isoDateSchema.optional(),
  version: z.number().int().min(1).optional(),
});
export const scenarioSimulationSchema = z.object({
  installments: z.number().int(),
  messages: z.number().int(),
  escalations: z.number().int(),
  notStarted: z.number().int(),
  estimatedCostMillimes: z.number().int(),
  unitCostMillimes: z.record(z.string(), z.number().int()),
  calendar: z.array(
    z.object({
      date: z.string(),
      total: z.number().int(),
      byChannel: z.record(z.string(), z.number().int()),
    }),
  ),
});

// --- Modèles ---

export const templateSchema = z.object({
  id: z.string(),
  name: z.string(),
  channel: z.enum(CHANNELS),
  language: z.enum(LANGUAGES),
  body: z.string(),
  variables: z.array(z.string()),
  insurerId: z.string().nullable(),
  connectTemplateId: z.string().nullable(),
  status: z.enum(['draft', 'pending', 'approved', 'rejected']),
  /** Motif de refus de Beta Connect. */
  reviewNote: z.string().nullable(),
});
export const templateListSchema = z.object({ items: z.array(templateSchema) });
export const templateQuerySchema = z.object({
  channel: z.enum(CHANNELS).optional(),
  language: z.enum(LANGUAGES).optional(),
  name: z.string().max(64).optional(),
});

const templateName = z.string().regex(/^[a-z0-9_]{1,64}$/, 'Nom : a-z, 0-9 et _ (64 au plus)');

export const templateCreateSchema = z.object({
  name: templateName,
  channel: z.enum(CHANNELS),
  language: z.enum(LANGUAGES),
  body: z.string().trim().min(1).max(4000),
  insurerId: z.string().nullable().optional(),
});
export const templatePatchSchema = z.object({ body: z.string().trim().min(1).max(4000) }).strict();

export const templatePreviewRequestSchema = z.object({
  variables: z.record(z.string(), z.string().max(500)).optional(),
});
export const templatePreviewSchema = z.object({ text: z.string(), missing: z.array(z.string()) });

// --- Mode d'envoi, journal des messages, séquences (BA-018, BA-019) ---

/** Sortie du mode brouillon : confirmation explicite exigée (spec 05.8). */
export const sendingModeSchema = z.object({
  draftMode: z.boolean(),
  confirm: z.literal('ACTIVER').optional(),
});

export const messageLogSchema = z.object({
  id: z.string(),
  policyholderId: z.string(),
  installmentId: z.string().nullable(),
  sequenceId: z.string().nullable(),
  channel: z.enum(CHANNELS),
  direction: z.enum(['out', 'in']),
  /** `simulated` : aurait été envoyé (mode brouillon). */
  status: z.string(),
  statusDetail: z.string().nullable(),
  templateId: z.string().nullable(),
  costMillimes: z.number().int().nullable(),
  sentAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  failedAt: z.string().nullable(),
});

export const messageQuerySchema = z.object({
  installmentId: z.string().optional(),
  policyholderId: z.string().optional(),
  sequenceId: z.string().optional(),
  channel: z.enum(CHANNELS).optional(),
  status: z.string().max(20).optional(),
  cursor: z.string().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const messagePageSchema = z.object({
  items: z.array(messageLogSchema),
  nextCursor: z.string().nullable(),
});

export const sequenceSchema = z.object({
  id: z.string(),
  installmentId: z.string(),
  scenarioId: z.string(),
  scenarioVersion: z.number().int(),
  status: z.enum([
    'scheduled',
    'running',
    'paused',
    'stopped_paid',
    'stopped_optout',
    'stopped_manual',
    'escalated',
    'completed',
  ]),
  currentStepNo: z.number().int(),
  nextRunAt: z.string().nullable(),
  pausedUntil: z.string().nullable(),
  stopReason: z.string().nullable(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  events: z.array(
    z.object({
      stepNo: z.number().int(),
      kind: z.string(),
      channel: z.enum(CHANNELS).nullable(),
      messageLogId: z.string().nullable(),
      payload: z.unknown().nullable(),
      at: z.string(),
    }),
  ),
});
